package com.dotdrives.web.pages.forms

import com.dotdrives.model.FormAmount
import react.FC
import react.Key
import react.Props
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.span
import react.useEffect
import react.useState
import web.cssom.ClassName
import web.html.ButtonType
import web.html.InputType
import web.html.button as buttonType
import web.html.text

internal fun amountInputValue(amount: Double): String = when {
    amount == 0.0 -> ""
    amount.isFinite() && amount == amount.toLong().toDouble() -> amount.toLong().toString()
    else -> amount.toString()
}

internal external interface FormAmountValueProps : Props {
    var amount: Double
    var index: Int
    var canWrite: Boolean
    var onChange: (Double) -> Unit
}

private val FormAmountValue = FC<FormAmountValueProps> { props ->
    val (text, setText) = useState(amountInputValue(props.amount))
    useEffect(props.amount) {
        if (text.toDoubleOrNull() != props.amount && !(text.toDoubleOrNull() == null && props.amount == 0.0)) setText(amountInputValue(props.amount))
    }
    div {
        className = ClassName("forms-v2-money-input")
        span { ariaHidden = true; +"$" }
        input {
            type = InputType.text; value = text; ariaLabel = "Amount ${props.index + 1}"
            placeholder = "0"; disabled = !props.canWrite
            onChange = {
                if (props.canWrite) {
                    val raw = it.currentTarget.value
                    setText(raw)
                    props.onChange(raw.toDoubleOrNull() ?: 0.0)
                }
            }
        }
    }
}

val FormAmountEditor = FC<FormTabProps> { props ->
    val state = props.state
    val controller = props.controller
    // Config amounts have no persisted IDs. Keep UI identity separate from numeric values so
    // incomplete input and focus follow a row when it moves, including duplicate amounts.
    val (rowKeys, setRowKeys) = useState { state.draftAmounts.indices.toList() }
    val (nextKey, setNextKey) = useState(state.draftAmounts.size)
    div {
        className = ClassName("forms-v2-amount-list")
        state.draftAmounts.forEachIndexed { index, amount ->
            div {
                key = (rowKeys.getOrNull(index)?.toString() ?: "external-$index").unsafeCast<Key>()
                className = ClassName("forms-v2-amount-row" + if (amount.isDefault) " forms-v2-amount-default" else "")
                div {
                    className = ClassName("forms-v2-reorder")
                    listOf(-1 to "↑", 1 to "↓").forEach { (delta, arrow) ->
                        button {
                            type = ButtonType.buttonType; ariaLabel = "Move amount ${index + 1} ${if (delta < 0) "up" else "down"}"
                            disabled = !props.canWrite || index + delta !in state.draftAmounts.indices
                            onClick = {
                                val destination = index + delta
                                if (props.canWrite && destination in state.draftAmounts.indices) {
                                    val keys = rowKeys.toMutableList()
                                    keys.add(destination, keys.removeAt(index))
                                    setRowKeys(keys)
                                    controller.moveAmount(index, destination)
                                }
                            }; +arrow
                        }
                    }
                }
                FormAmountValue {
                    this.amount = amount.amount; this.index = index; canWrite = props.canWrite
                    onChange = { next -> if (props.canWrite) controller.setAmounts(state.draftAmounts.mapIndexed { i, a -> if (i == index) a.copy(amount = next) else a }) }
                }
                input {
                    type = InputType.text; className = ClassName("forms-v2-amount-label")
                    value = amount.label; placeholder = "What it funds (optional)"; ariaLabel = "Amount ${index + 1} label"
                    disabled = !props.canWrite
                    onChange = { event -> if (props.canWrite) controller.setAmounts(state.draftAmounts.mapIndexed { i, a -> if (i == index) a.copy(label = event.currentTarget.value) else a }) }
                }
                button {
                    type = ButtonType.buttonType; className = ClassName("forms-v2-default-star")
                    ariaLabel = if (amount.isDefault) "Amount ${index + 1} is the default" else "Make amount ${index + 1} the default"
                    ariaPressed = amount.isDefault.unsafeCast<react.dom.aria.AriaPressed>()
                    title = "Preselected for donors"; disabled = !props.canWrite
                    onClick = { if (props.canWrite) controller.setAmounts(state.draftAmounts.mapIndexed { i, a -> a.copy(isDefault = i == index) }) }; +"★"
                }
                button {
                    type = ButtonType.buttonType; className = ClassName("forms-v2-icon-button")
                    ariaLabel = "Remove amount ${index + 1}"; disabled = !props.canWrite
                    onClick = {
                        if (props.canWrite) {
                            setRowKeys(rowKeys.filterIndexed { i, _ -> i != index })
                            controller.setAmounts(state.draftAmounts.filterIndexed { i, _ -> i != index })
                        }
                    }; +"×"
                }
            }
        }
    }
    button {
        type = ButtonType.buttonType; className = ClassName("forms-v2-add")
        disabled = !props.canWrite
        onClick = {
            if (props.canWrite) {
                setRowKeys(rowKeys + nextKey); setNextKey(nextKey + 1)
                controller.setAmounts(state.draftAmounts + FormAmount(0.0, "", state.draftAmounts.isEmpty()))
            }
        }
        +"+ Add amount"
    }
    div { className = ClassName("forms-v2-help"); +"Use the star to preselect an amount. Arrow buttons reorder choices." }
    ToggleRow { name = "Monthly giving"; sub = "Show the recurring option"; on = state.draftToggles.monthlyGiving; enabled = props.canWrite; onToggle = { if (props.canWrite) controller.setToggles(state.draftToggles.copy(monthlyGiving = it)) } }
    ToggleRow { name = "Quarterly giving"; sub = "Show a quarterly recurring option"; on = state.draftToggles.quarterlyGiving; enabled = props.canWrite; onToggle = { if (props.canWrite) controller.setToggles(state.draftToggles.copy(quarterlyGiving = it)) } }
    ToggleRow { name = "Custom amount"; sub = "Allow an Other amount entry"; on = state.draftToggles.customAmount; enabled = props.canWrite; onToggle = { if (props.canWrite) controller.setToggles(state.draftToggles.copy(customAmount = it)) } }
    ToggleRow { name = "Cover processing fees"; sub = "Offer the configured optional contribution when available"; on = state.draftToggles.coverFees; enabled = props.canWrite; onToggle = { if (props.canWrite) controller.setToggles(state.draftToggles.copy(coverFees = it)) } }
    ToggleRow { name = "Anonymous gifts"; sub = "Hide donor names from public feeds"; on = state.draftToggles.anonymousGifts; enabled = props.canWrite; onToggle = { if (props.canWrite) controller.setToggles(state.draftToggles.copy(anonymousGifts = it)) } }
}
